import React, { useState, useCallback } from 'react';
import { AppError, RecoveryAction } from '../../types/error';
import { ErrorManager } from '../../utils/errorManager';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/components/ErrorDisplay.css';

interface ErrorDisplayProps {
  error: AppError | null;
  onRetry?: () => void | Promise<void>;
  onDismiss?: () => void;
  className?: string;
  showDetails?: boolean;
  compact?: boolean;
}

/**
 * Universal error display component with recovery actions
 */
const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  onDismiss,
  className = '',
  showDetails = false,
  compact = false
}) => {
  const { t } = useLanguage();
  const [isRetrying, setIsRetrying] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  const handleRetry = useCallback(async () => {
    if (!onRetry || isRetrying) return;

    setIsRetrying(true);
    try {
      await onRetry();
    } catch (retryError) {
      console.error('Retry failed:', retryError);
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry, isRetrying]);

  const handleRecoveryAction = useCallback(async (action: RecoveryAction) => {
    switch (action.type) {
      case 'retry':
        await handleRetry();
        break;
      case 'redirect':
        if (action.url) {
          window.location.href = action.url;
        }
        break;
      case 'refresh':
        window.location.reload();
        break;
      case 'contact_support':
        window.open('/contact', '_blank');
        break;
      case 'manual_action':
        if (action.action) {
          await action.action();
        }
        break;
    }
  }, [handleRetry]);

  if (!error) return null;

  const errorInfo = ErrorManager.createUserErrorMessage(error);
  const severityClass = `error-${errorInfo.severity}`;
  const sizeClass = compact ? 'error-compact' : 'error-full';

  return (
    <div className={`error-display ${severityClass} ${sizeClass} ${className}`} role="alert">
      <div className="error-header">
        <div className="error-icon">
          {errorInfo.severity === 'critical' && <i className="bi bi-exclamation-triangle-fill"></i>}
          {errorInfo.severity === 'high' && <i className="bi bi-exclamation-circle-fill"></i>}
          {errorInfo.severity === 'medium' && <i className="bi bi-exclamation-circle"></i>}
          {errorInfo.severity === 'low' && <i className="bi bi-info-circle"></i>}
        </div>
        <div className="error-content">
          <div className="error-title">{errorInfo.title}</div>
          <div className="error-message">{errorInfo.message}</div>
        </div>
        {onDismiss && (
          <button 
            className="error-dismiss"
            onClick={onDismiss}
            aria-label={t('common.close')}
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>

      {!compact && errorInfo.actions.length > 0 && (
        <div className="error-actions">
          {errorInfo.actions.map((action, index) => (
            <button
              key={index}
              className={`error-action error-action-${action.type}`}
              onClick={() => handleRecoveryAction(action)}
              disabled={isRetrying && action.type === 'retry'}
              title={action.description}
            >
              {action.icon && <i className={`bi ${action.icon}`}></i>}
              {isRetrying && action.type === 'retry' ? (
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
              ) : null}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {showDetails && (
        <div className="error-details">
          <button
            className="error-details-toggle"
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
          >
            <i className={`bi bi-chevron-${showTechnicalDetails ? 'up' : 'down'}`}></i>
            {t('errors.technicalDetails') || 'Technical Details'}
          </button>
          
          {showTechnicalDetails && (
            <div className="error-technical-details">
              <div className="detail-item">
                <strong>Error Code:</strong> {error.code}
              </div>
              <div className="detail-item">
                <strong>Category:</strong> {error.category}
              </div>
              <div className="detail-item">
                <strong>Technical Message:</strong> {error.technicalMessage}
              </div>
              {error.context?.timestamp && (
                <div className="detail-item">
                  <strong>Timestamp:</strong> {error.context.timestamp.toISOString()}
                </div>
              )}
              {error.context?.url && (
                <div className="detail-item">
                  <strong>Page:</strong> {error.context.url}
                </div>
              )}
              {process.env.NODE_ENV === 'development' && error.originalError?.stack && (
                <div className="detail-item">
                  <strong>Stack Trace:</strong>
                  <pre className="error-stack">{error.originalError.stack}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ErrorDisplay;
